import type { Localized } from '../types/notice'

/**
 * What an account is allowed to see in analytics.
 * - `city`: whole territory (mayor, general management)
 * - `district`: only the requests of one electoral district
 * - `directions`: only the departments the person manages
 */
export type AccountScope =
  | { kind: 'city' }
  | { kind: 'district'; district: number; sectors: string[] }
  | { kind: 'directions'; directions: string[] }

export interface Account {
  id: string
  firstName: string
  lastName: string
  /** Job note — not shown in the menu, used to scope analytics later. */
  role: Localized
  scope: AccountScope
  /** Where the info was checked (brossard.ca, September 2026). */
  source: string
}

export const ACCOUNTS: Account[] = [
  {
    id: 'doreen-assaad',
    firstName: 'Doreen',
    lastName: 'Assaad',
    role: { fr: 'Mairesse de Brossard', en: 'Mayor of Brossard' },
    scope: { kind: 'city' },
    source: 'brossard.ca/elus-municipaux — réélue en 2017, 2021 et 2025',
  },
  {
    id: 'stephanie-quintal',
    firstName: 'Stéphanie',
    lastName: 'Quintal',
    role: {
      fr: 'Conseillère municipale — District 4',
      en: 'City councillor — District 4',
    },
    // Official page: "District 4 · Secteur A" (elected Nov. 2025).
    scope: { kind: 'district', district: 4, sectors: ['A'] },
    source: 'brossard.ca/elus-et-membres-des-comites/stephanie-quintal',
  },
  {
    id: 'guy-benedetti',
    firstName: 'Guy',
    lastName: 'Benedetti',
    role: { fr: 'Directeur général', en: 'Chief Administrative Officer' },
    scope: { kind: 'city' },
    source: 'Procès-verbaux du conseil 2025 — « M. Guy Benedetti, directeur général »',
  },
  {
    id: 'martin-brunelle',
    firstName: 'Martin',
    lastName: 'Brunelle',
    role: {
      fr: 'Directeur principal — Direction principale administration et efficience',
      en: 'Senior Director — Administration and Efficiency Division',
    },
    // Appointed Oct. 2025; oversees Finances, Greffe, TI and Services Brossard.
    scope: {
      kind: 'directions',
      directions: ['finances', 'greffe', 'technologies-information', 'services-brossard'],
    },
    source: 'brossard.ca/elus-et-membres-des-comites/martin-brunelle',
  },
  {
    id: 'christian-gaudette',
    firstName: 'Christian',
    lastName: 'Gaudette',
    role: {
      fr: 'Conseiller municipal — District 1',
      en: 'City councillor — District 1',
    },
    // Official page lists sectors C, E and L for district 1.
    scope: { kind: 'district', district: 1, sectors: ['C', 'E', 'L'] },
    source: 'brossard.ca/elus-municipaux',
  },
]

export const DEFAULT_ACCOUNT_ID = 'doreen-assaad'
